/**
 * JWT Utilities for decoding and extracting claims from tokens
 */

interface JWTPayload {
  [key: string]: any;
  exp?: number;
  iat?: number;
  sub?: string;
  user_id?: number | string;
  id?: number | string;
}

/**
 * Decode a JWT token without verification
 * @param token - The JWT token string
 * @returns Decoded payload or null if invalid
 */ 
export const decodeJWT = (token: string): JWTPayload | null => {
  try {
    if (!token) return null;

    // JWT format: header.payload.signature
    const parts = token.split(".");
    if (parts.length !== 3) {
      console.error("Invalid JWT format");
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    const decoded = JSON.parse(atob(payload));

    return decoded;
  } catch (error) {
    console.error("Error decoding JWT:", error);
    return null;
  }
};

/**
 * Get user ID from access token
 * @param token - The access token
 * @returns User ID or null if not found
 */
export const getUserIdFromToken = (token: string): number | string | null => {
  const payload = decodeJWT(token);
  
  if (!payload) return null;

  // Try common user ID field names
  return payload.user_id || payload.id || payload.sub || payload.user?.id || null;
};

/**
 * Check if JWT token is expired
 * @param token - The JWT token
 * @returns true if expired, false if valid
 */
export const isTokenExpired = (token: string): boolean => {
  const payload = decodeJWT(token);
  
  if (!payload || !payload.exp) return false;

  // exp is in seconds, convert to milliseconds
  const expirationTime = payload.exp * 1000;
  const currentTime = Date.now();

  return currentTime > expirationTime;
};

/**
 * Get all claims from token
 * @param token - The JWT token
 * @returns Full payload or null if invalid
 */
export const getTokenClaims = (token: string): JWTPayload | null => {
  return decodeJWT(token);
};

/**
 * Extract specific claim from token
 * @param token - The JWT token
 * @param claim - The claim name to extract
 * @returns Claim value or null
 */
export const getTokenClaim = (token: string, claim: string): any => {
  const payload = decodeJWT(token);
  return payload ? payload[claim] : null;
};
